"use client"
import { BottomSheet } from "@opensea/ui-kit"
import { useSnapshot } from "valtio"
import { FlowActions, FlowState } from "../../stores/flowStore"
import type { FlowStateVariants, FlowType } from "../flow-state"
import { AcceptOfferFlow } from "./AcceptOfferFlow/AcceptOfferFlow"
import { BottomSheetErrorBoundary } from "./BottomSheetErrorBoundary"
import { CancelAllOrdersFlow } from "./CancelAllOrdersFlow/CancelAllOrdersFlow"
import { CancelOrderFlow } from "./CancelOrderFlow/CancelOrderFlow"
import { CheckoutFlow } from "./CheckoutFlow/CheckoutFlow"
import { CollectionSweepFlow } from "./CollectionSweepFlow/CollectionSweepFlow"
import { CreateCollectionOfferFlow } from "./CreateCollectionOfferFlow/CreateCollectionOfferFlow"
import { CreateOfferFlow } from "./CreateOfferFlow/CreateOfferFlow"
import { DropPublishFlow } from "./DropPublishFlow/DropPublishFlow"
import { ListingFlow } from "./ListingFlow/ListingFlow"
import { MintFlow } from "./MintFlow/MintFlow"
import { TokenClaimFlow } from "./TokenClaimFlow/TokenClaimFlow"
import { TransferFlow } from "./TransferFlow/TransferFlow"

export const BottomSheetFlows = () => {
  const { flow } = useSnapshot(FlowState)
  // Cast to remove readonly from valtio snapshot
  const mutableFlow = flow as FlowStateVariants | undefined

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      FlowActions.endFlow()
    }
  }

  const isBottomSheetFlowActive = isBottomSheetFlow(mutableFlow)

  return (
    <BottomSheet
      content={
        <BottomSheetErrorBoundary>
          <FlowContent flow={mutableFlow} />
        </BottomSheetErrorBoundary>
      }
      onOpenChange={handleOpenChange}
      open={isBottomSheetFlowActive}
    />
  )
}

const FlowContent = ({ flow }: { flow: FlowStateVariants | undefined }) => {
  switch (flow?.type) {
    case "checkout":
      return <CheckoutFlow {...flow} />
    case "token-claim":
      return <TokenClaimFlow {...flow} />
    case "collection-sweep":
      return <CollectionSweepFlow {...flow} />
    case "accept-offer":
      return <AcceptOfferFlow {...flow} />
    case "cancel-order":
      return <CancelOrderFlow {...flow} />
    case "listing":
      return <ListingFlow {...flow} />
    case "create-offer":
      return <CreateOfferFlow {...flow} />
    case "create-collection-offer":
      return <CreateCollectionOfferFlow {...flow} />
    case "transfer":
      return <TransferFlow {...flow} />
    case "cancel-all-orders":
      return <CancelAllOrdersFlow {...flow} />
    case "mint":
      return <MintFlow {...flow} />
    case "drop-publish":
      return <DropPublishFlow {...flow} />
    default:
      return null
  }
}

const BOTTOM_SHEET_FLOWS = new Set<FlowType>([
  "checkout",
  "token-claim",
  "collection-sweep",
  "accept-offer",
  "cancel-order",
  "transfer",
  "listing",
  "create-offer",
  "create-collection-offer",
  "cancel-all-orders",
  "mint",
  "drop-publish",
])

function isBottomSheetFlow(flow: FlowStateVariants | undefined): boolean {
  return !!flow && BOTTOM_SHEET_FLOWS.has(flow.type)
}
